import React, { useEffect, useState } from 'react';
import { FieldArray, Form, Formik } from 'formik';
import { Check, Search } from 'lucide-react';
import DateTimePicker from '../WokingHour/DateTimePicker';
import { useDispatch, useSelector } from 'react-redux';
import { getWorkingHourUSer, updateWorkingHourUSer } from 'slices/calendar/thunk';
import ConfirmModal from 'Common/ConfirmModal';
import BreakTimeComponent from '../WokingHour/BreakTimeComponent';
import Loading from 'Common/Components/Loading';

const WorkingHour: React.FC = () => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const dispatch = useDispatch<any>();
  const [showModal, setShowModal] = useState(false);
  const [payloadData, setPayloadData] = useState<any>();
  const [data, setData] = useState<any[]>([]);
  const [breakingTimeData, setBreakingTimeData] = useState<any>();

  const workingHours = useSelector((state: any) => state.Calendar.workingHours);
  const breakingTime = useSelector((state: any) => state.Calendar.breakingTime);
  const loading = useSelector((state: any) => state.Calendar.loading);

  useEffect(() => {
    dispatch(getWorkingHourUSer());
  }, [dispatch]);

  useEffect(() => {
    if (!!breakingTime?.Id) {
      setBreakingTimeData(breakingTime);
    }
  }, [breakingTime]);
  useEffect(() => {
    if (Array.isArray(workingHours) && workingHours.length > 0) {
      const convertedWorkingHours = workingHours.map((item: any) => ({
        ...item,
        startTime: item?.startTime && item?.startTime,
        endTime: item?.endTime && item?.endTime,
      }));

      if (convertedWorkingHours) {
        const sortedData = convertedWorkingHours.sort((a: any, b: any) =>
          (a.day || a.dayOfWeek) - (b.day || b.dayOfWeek)
        );
        console.log('Converted working hours:', sortedData);
        setData(sortedData);
      }
    }

    if (breakingTime) {
      setBreakingTimeData({
        ...breakingTime,
        from: breakingTime?.from && breakingTime.from,
        to: breakingTime?.to && breakingTime.to,
      });
    }
  }, [workingHours, breakingTime]);

  const handleConfirm = () => {
    dispatch(updateWorkingHourUSer(payloadData)).then(() => {
      dispatch(getWorkingHourUSer());
    });
    setShowModal(false);
  };

  const handleHide = () => {
    setShowModal(false);
  };

  // Initial values for Formik
  const initialWorkingHour = {
    workingHours: data.map((item: any) => ({
      day: item.dayOfWeek,
      isAvailable: item.isAvailable,
      fromTime: item.startTime,
      toTime: item.endTime,
    })),
    breakingTime: {
      id: breakingTimeData?.id || null,
      isAvailable: breakingTimeData?.isAvailable || false,
      fromTime: breakingTimeData?.from || '12:00',
      toTime: breakingTimeData?.to || '15:00',
    }
  };

  // Handle form submission
  const handleSubmit = async (values: typeof initialWorkingHour) => {
    setShowModal(true);
    const payload = {
      workingHours: values.workingHours.map((wh) => ({
        ...wh,
        fromTime: wh.fromTime,
        toTime: wh.toTime,
      })),
      breakingTime: values.breakingTime.isAvailable
        ? {
          ...values.breakingTime,
          fromTime: values.breakingTime.fromTime,
          toTime: values.breakingTime.toTime, timeZone,
        }
        : values.breakingTime,
    };

    if (payload) {
      setPayloadData(payload);
    }
  };

  if (loading) {
    return (
      <div className="bg-white m-auto flex items-center justify-center">
        <Loading size="sm" color="blue" isVisible={true} />
      </div>
    );
  }

  if (!workingHours?.length) {
    return (
      <div className="noresult m-auto flex items-center justify-center">
        <div className="py-6 text-center">
          <Search className="size-6 mx-auto text-slate-500 fill-sky-100 dark:sky-500/20" />
          <h6 className="mt-2 mb-1">No Result Found</h6>
        </div>
      </div>
    );
  } return (
    <>
      <div id="workingHour-content" className="mx-2 sm:px-3 mt-4 sm:mt-6 lg:mt-[50px] pb-4 overflow-auto">
        <Formik
          enableReinitialize
          initialValues={initialWorkingHour}
          onSubmit={handleSubmit}
        >
          {({ values }) => (
            <Form className="min-h-0 flex-1 w-full overflow-y-auto">
              <div
                className="hidden sm:block mt-3 flex items-center justify-between gap-4 sm:gap-10 transition-opacity duration-1000 ease-in-out"
                style={{
                  height: '40px',
                  opacity: JSON.stringify(values) !== JSON.stringify(initialWorkingHour) ? 1 : 0,
                  visibility: JSON.stringify(values) !== JSON.stringify(initialWorkingHour) ? 'visible' : 'hidden',
                }}
              >
                {values.workingHours.length > 0 && JSON.stringify(values) !== JSON.stringify(initialWorkingHour) && (
                  <button
                    type="submit"
                    className="sm:w-auto w-full text-xs sm:text-sm text-white btn bg-blue-500 hover:bg-blue-600 hover:text-white px-3 py-2"
                  >
                    <Check className="inline-block size-4" /> <span className="align-middle">Save Changes</span>
                  </button>
                )}
              </div>
              <div className="pt-2 transition-transform duration-300 ease-in-out w-full">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-3 sm:gap-4 w-full">
                  <FieldArray name="workingHours">
                    {() => {
                      console.log('Working hours data:', values.workingHours);
                      return values.workingHours.map((workingHour: any, index: number) => (
                        <DateTimePicker
                          key={`${workingHour.day}-${index}`}
                          day={workingHour.day ?? ''}
                          index={index}
                        />
                      ));
                    }}
                  </FieldArray>
                </div>
              </div>
              <hr className="mt-4 sm:mt-5 grid grid-cols-1" />
              {breakingTime?.id && (
                <div className="mt-4 sm:mt-5">
                  <BreakTimeComponent
                    startWorkingHour={
                      values.workingHours.length > 0 &&
                      values.workingHours.filter((item: any) => item.isAvailable)[0]?.fromTime
                    }
                    endWorkingHour={
                      values.workingHours.length > 0 &&
                      values.workingHours.filter((item: any) => item.isAvailable)[0]?.toTime
                    }
                  />
                </div>
              )}
              <div
                className="sm:hidden mt-3 flex items-center justify-between gap-4 sm:gap-10 transition-opacity duration-1000 ease-in-out"
                style={{
                  height: '40px',
                  opacity: JSON.stringify(values) !== JSON.stringify(initialWorkingHour) ? 1 : 0,
                  visibility: JSON.stringify(values) !== JSON.stringify(initialWorkingHour) ? 'visible' : 'hidden',
                }}
              >
                {values.workingHours.length > 0 && JSON.stringify(values) !== JSON.stringify(initialWorkingHour) && (
                  <button
                    type="submit"
                    className="sm:w-auto w-full text-xs sm:text-sm text-white btn bg-blue-500 hover:bg-blue-600 hover:text-white px-3 py-2"
                  >
                    <Check className="inline-block size-4" /> <span className="align-middle">Save Changes</span>
                  </button>
                )}
              </div>
            </Form>
          )}
        </Formik>
      </div>

      <ConfirmModal
        show={showModal}
        onHide={handleHide}
        onConfirm={handleConfirm}
        title="You are about to update the working hours and break times"
        content="This will affect staff scheduling availability"
      />
    </>
  );
};

export default WorkingHour;
